import './index.scss';
import LogoColor from 'src/assets/svg/logo_color.svg?react';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Col, Collapse, Divider, Form, Input, Row, Spin, message } from 'antd';
import { InfoCircleOutlined, LockOutlined, MailOutlined } from '@ant-design/icons';
import { nameof } from 'src/shared/utils/objects';
import { IconChevronLeft } from '@tabler/icons-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { Title, Text, Link } from 'src/shared/components/typography';
import { isNullOrWhiteSpaces } from 'src/shared/utils/strings';
import { useAuth } from 'react-oidc-context';
import { useMount } from 'ahooks';
import { useBackHandler } from 'src/common/use-back-handler';

const EnterpriseUserPasswordView: FC = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const handleBack = useBackHandler();
  const { search } = useLocation();

  const qs = new URLSearchParams(search ?? '');
  const { user: userName, code, email, returnUrl } = Object.fromEntries(qs);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  useMount(() => {
    const isEmailConfirmedParam = 'emailConfirmed';
    const isEmailConfirmed = searchParams.get(isEmailConfirmedParam) ?? '';

    if (isEmailConfirmed === 'True') message.success(t('Correo verificado'));
    else if (isEmailConfirmed === 'False') message.error(t('Error al verificar el correo'));

    setSearchParams((s) => {
      const newSearchParams = new URLSearchParams(s);
      newSearchParams.delete(isEmailConfirmedParam);
      return newSearchParams;
    });
  });

  const onFinish = async () => {
    setErrorMsg(undefined);
    setIsLoading(true);

    try {
      const result = await axios.post<any>('/account/resetEnterpriseUserPassword', {
        userName: userName,
        password: password,
        confirmPassword: password,
        code: code
      });

      if (result?.status !== 200) {
        console.error('ResetPassword', result);
        const msg = result?.data?.message ?? (result as any as AxiosError<{ message: string }>)?.response?.data?.message ?? t('Error inesperado');
        setErrorMsg(msg);
        setIsLoading(false);
      } else {
        window.location.href = returnUrl;
      }
    } catch (e) {
      console.error('ResetPassword', e);
      const axiosError = e as AxiosError<{ message: string }>;
      setErrorMsg(axiosError?.response?.data?.message?.toString() ?? (t('Error inesperado') as string));
    } finally {
      setIsLoading(false);
    }
  };

  const [password, setPassword] = useState<string>('');
  const [meter, setMeter] = useState<boolean>((password || '').length > 0);

  const atLeastOneUppercase = /[A-Z]/g; // capital letters from A to Z
  const atLeastOneLowercase = /[a-z]/g; // small letters from a to z
  const atLeastOneNumeric = /[0-9]/g; // numbers from 0 to 9
  const atLeastOneSpecialChar = /[~@#_^*%/.+:;=,!?<>()[\]{}'"\\\-&|$]/g; // any of the special characters within the square brackets
  const eightCharsOrMore = /.{8,}/g; // eight characters or more

  const passwordTracker = {
    uppercase: password.match(atLeastOneUppercase),
    lowercase: password.match(atLeastOneLowercase),
    number: password.match(atLeastOneNumeric),
    specialChar: password.match(atLeastOneSpecialChar),
    eightCharsOrGreater: password.match(eightCharsOrMore)
  };

  const passwordStrength = Object.values(passwordTracker).filter((value) => value).length;

  const buildMessage = () => {
    if (passwordStrength >= 5) return '';
    const parts: string[] = [];

    if (!passwordTracker.eightCharsOrGreater) parts.push(t('8 caracteres alfanuméricos'));
    if (!passwordTracker.uppercase) parts.push(t('una mayúscula'));
    if (!passwordTracker.lowercase) parts.push(t('una minúscula'));
    if (!passwordTracker.specialChar) parts.push(t('un caracter especial'));
    if (!passwordTracker.number) parts.push(t('un número'));

    const last = parts.pop();
    return t('Debe contener al menos ') + parts.join(', ') + (parts.length > 0 ? t(' y ') : '') + last;
  };

  return (
    <div id="reset-password-container">
      <div id="logo-container">
        <span title="back-cta" role="img" id="back-cta" className={isLoading ? 'anticon disabled' : 'anticon'}>
          <IconChevronLeft onClick={isLoading ? undefined : handleBack} size={20} strokeWidth={3} color={isLoading ? 'lightgrey' : '#22817a'} />
        </span>
        <LogoColor />
      </div>
      <div id="form-container">
        <Spin spinning={isLoading} size={'large'}>
          <Card>
            <>
              <Title style={{ fontSize: 24, marginBottom: 32 }} level={4}>
                {t('¡Hola! Únete y accede al mejor talento para tu empresa.')}
              </Title>
              {!isNullOrWhiteSpaces(errorMsg) && (
                <Row style={{ marginTop: 24 }}>
                  <Col span={24}>
                    <Alert closable onClose={() => setErrorMsg(undefined)} description={errorMsg} type="error" />
                  </Col>
                </Row>
              )}

              <Form id="registration" name="registration" style={{ maxWidth: '100%', marginTop: 36 }} onFinish={onFinish}>
                <Row gutter={[0, 32]} style={{ marginTop: 36 }}>
                  <Col span={24}>
                    <Input
                      className="j2l-form-big-input"
                      disabled
                      size="large"
                      placeholder={t('Correo electrónico')}
                      prefix={<MailOutlined />}
                      value={email}
                      type="email"
                      autoComplete="username"
                      name="username"
                      id="username"
                    />
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      style={{ marginBottom: 0 }}
                      extra={
                        meter && (
                          <div>
                            <div className="password-strength-meter"></div>
                            <div>{buildMessage()}</div>
                          </div>
                        )
                      }
                      name={nameof('password')}
                      rules={[{ required: true, message: t('Por favor ingrese una contraseña') as string }]}
                    >
                      <Input.Password
                        className="j2l-form-big-input"
                        disabled={isLoading}
                        size="large"
                        placeholder={t('Contraseña') as string}
                        prefix={<LockOutlined />}
                        onFocus={() => setMeter(true)}
                        onChange={(v) => setPassword(v.target.value)}
                        type="password"
                        name="password"
                        id="password"
                        autoComplete="new-password"
                      />
                    </Form.Item>
                  </Col>
                </Row>
                <Row style={{ marginTop: 20, marginBottom: 34 }}>
                  <Col span={24}>
                    <Collapse
                      className="password-suggestions-collapse-2"
                      size="large"
                      ghost
                      expandIcon={() => <InfoCircleOutlined style={{ fontSize: 16 }} />}
                      items={[
                        {
                          key: '1',
                          label: (
                            <Text fontSize={16} underline>
                              {t('Crea una contraseña indescifrable')}
                            </Text>
                          ),
                          children: (
                            <ul>
                              <li>{t('Mejor única, sólo para jobs2live.')}</li>
                              <li>{t('Qué sea larga, cuanto más mejor. Y que combine letras, números y símbolos.')}</li>
                              <li>{t('No uses información personal como fechas o nombres.')}</li>
                              <li>{t('Una buena opción es usar una frase fácil de recordar. Por ejemplo "LaT0rtilla,SiempreConCebolla".')}</li>
                            </ul>
                          )
                        }
                      ]}
                    />
                  </Col>
                </Row>

                <Row>
                  <Col span={24}>
                    <Button
                      shape="round"
                      style={{
                        width: '100%',
                        border: 'none',
                        backgroundColor: passwordStrength < 5 || isLoading ? '#F1EDE9' : undefined,
                        color: passwordStrength < 5 || isLoading ? '#CBCED4' : undefined
                      }}
                      size="large"
                      type="primary"
                      disabled={passwordStrength < 5 || isLoading}
                      htmlType="submit"
                    >
                      {t('Dame de alta')}
                    </Button>
                  </Col>
                </Row>

                <Divider style={{ marginTop: 40, marginBottom: 35 }} />
                <Row>
                  <Col span={24}>
                    <Text fontSize={16}>{t('¿Ya eres usuario de jobs2live?')}</Text>{' '}
                    <Link onClick={() => auth?.signinRedirect()} disabled={isLoading} color="#1A1C21" fontSize={16} underline>
                      {t('Inicia sesión')}
                    </Link>
                  </Col>
                </Row>

                <style>
                  {`
                  .password-strength-meter::before {
                    background-color: ${['red', 'orange', '#03a2cc', '#03a2cc', '#0ce052'][passwordStrength - 1] || ''};
                    width: ${(passwordStrength / 5) * 100}%;
                  }
                `}
                </style>
              </Form>
            </>
          </Card>
        </Spin>
      </div>
    </div>
  );
};

export default EnterpriseUserPasswordView;
